import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "@phosphor-icons/react";

const STORAGE_KEY = "pixora_cookie_consent_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay a bit so the page finishes loading before the banner appears
    const t = setTimeout(() => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChoice = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, date: new Date().toISOString() })
    );
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          role="dialog"
          aria-label="Consentement aux cookies"
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60]"
        >
          <div className="relative rounded-2xl bg-[#0b0b0b]/95 backdrop-blur-md border border-[#C9A962]/40 shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-5 sm:p-6">
            <button
              type="button"
              aria-label="Fermer et refuser"
              onClick={() => handleChoice("declined")}
              className="absolute top-3 right-3 text-[#8A7A5A] hover:text-[#C9A962] transition-colors"
              data-testid="cookie-close"
            >
              <X size={18} weight="bold" />
            </button>

            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#C9A962]/15 border border-[#C9A962]/40 flex items-center justify-center">
                <Cookie size={20} weight="fill" className="text-[#C9A962]" />
              </div>
              <div>
                <h3 className="text-[#F5E6B8] font-semibold text-base leading-tight">
                  Un instant, on parle cookies 🍪
                </h3>
                <p className="text-[#8A7A5A] text-xs mt-0.5 tracking-wide">
                  Votre confidentialité compte
                </p>
              </div>
            </div>

            <p className="text-[#D8CCA8] text-sm leading-relaxed mb-4">
              Ce site utilise uniquement les cookies nécessaires à son bon fonctionnement.
              Aucun traceur publicitaire n'est déposé sans votre accord. Consultez notre{" "}
              <Link
                to="/politique-confidentialite"
                className="text-[#C9A962] hover:underline underline-offset-2"
                onClick={() => setVisible(false)}
              >
                politique de confidentialité
              </Link>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => handleChoice("accepted")}
                data-testid="cookie-accept"
                className="flex-1 py-2.5 px-4 rounded-full bg-gradient-to-br from-[#E6C97A] to-[#C9A962] hover:from-[#F0D68A] hover:to-[#D4B570] text-[#0b0b0b] font-semibold text-sm transition-all shadow-lg"
              >
                Tout accepter
              </button>
              <button
                type="button"
                onClick={() => handleChoice("declined")}
                data-testid="cookie-decline"
                className="flex-1 py-2.5 px-4 rounded-full bg-transparent border border-[#C9A962]/50 hover:border-[#C9A962] text-[#D8CCA8] hover:text-[#F5E6B8] font-medium text-sm transition-colors"
              >
                Refuser
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
